import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getReservations } from "../../../../../../apis/reservation";
import { getInventoryAvailability } from "../../../../../../apis/inventory";
import {
  RefreshIcon,
  CalendarIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  TrendingUpIcon,
  HomeIcon,
} from "@heroicons/react/outline";

const DEFAULT_START_DATE = "2025-09-26";

const formatDate = (d) => {
  try {
    return new Date(d).toLocaleDateString();
  } catch (e) {
    return d;
  }
};

const Overview = () => {
  const { id: resortId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [recentReservations, setRecentReservations] = useState([]);
  const [availabilitySummary, setAvailabilitySummary] = useState(null);
  const [availabilityError, setAvailabilityError] = useState(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAvailabilityError(null);

    const today = new Date();
    const isoToday = today.toISOString().split("T")[0];
    const dateToUse = isoToday >= DEFAULT_START_DATE ? isoToday : DEFAULT_START_DATE;

    try {
      const results = await Promise.allSettled([
        getReservations(resortId, "weekly", dateToUse, 1),
        getInventoryAvailability(resortId, "weekly", dateToUse),
      ]);

      const resResult = results[0];
      const invResult = results[1];

      if (resResult.status === "fulfilled") {
        const resResp = resResult.value;
        const reservations = (resResp && resResp.data && resResp.data.reservations) || [];
        setRecentReservations(reservations.slice(0, 5));
      } else {
        setRecentReservations([]);
        setError(resResult.reason?.message || "Failed to load reservations");
      }

      if (invResult.status === "fulfilled") {
        setAvailabilityError(null);
        const invResp = invResult.value;
        const invData = (invResp && invResp.data) || null;
        if (invData && Array.isArray(invData.calendar)) {
          const summary = invData.calendar.map((day) => {
            const availableCount = Array.isArray(day.room_availability)
              ? day.room_availability.reduce((acc, item) => {
                  const val = typeof item.available === "number" ? item.available : item.available ? 1 : 0;
                  return acc + val;
                }, 0)
              : 0;
            return {
              date: day.date,
              label: day.date_string || day.date,
              availableCount,
            };
          });
          setAvailabilitySummary({ dates: invData.dates || [], summary });
        } else {
          setAvailabilitySummary({ dates: invData?.dates || [], summary: [] });
        }
      } else {
        setAvailabilitySummary({ dates: [], summary: [] });
        setAvailabilityError(invResult.reason?.message || "Failed to load availability");
      }
    } catch (e) {
      setError(e.message || "Failed to load overview");
    } finally {
      setLoading(false);
    }
  }, [resortId]);

  useEffect(() => {
    if (resortId) fetchOverview();
  }, [resortId, fetchOverview]);

  const totalAvailable = availabilitySummary?.summary.reduce((acc, d) => acc + d.availableCount, 0) || 0;

  return (
    <div className="overview-container">
      {/* Header */}
      <div className="overview-header">
        <div>
          <h2 className="section-title">Property Overview</h2>
          <p className="section-subtitle">Real-time insights and recent activity</p>
        </div>
        <button onClick={fetchOverview} className="refresh-button" disabled={loading}>
          <RefreshIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh Data</span>
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p className="loading-text">Loading overview data...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="error-alert">
          <ExclamationCircleIcon className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card stat-card-blue">
              <div className="stat-icon-wrapper">
                <CalendarIcon className="stat-icon" />
              </div>
              <div>
                <p className="stat-label">Total Reservations</p>
                <h3 className="stat-value">{recentReservations.length}</h3>
                <p className="stat-change">
                  <TrendingUpIcon className="w-4 h-4" />
                  This week
                </p>
              </div>
            </div>

            <div className="stat-card stat-card-green">
              <div className="stat-icon-wrapper">
                <CheckCircleIcon className="stat-icon" />
              </div>
              <div>
                <p className="stat-label">Available Rooms</p>
                <h3 className="stat-value">{totalAvailable}</h3>
                <p className="stat-change">
                  <HomeIcon className="w-4 h-4" />
                  Next 7 days
                </p>
              </div>
            </div>

            <div className="stat-card stat-card-purple">
              <div className="stat-icon-wrapper">
                <UserGroupIcon className="stat-icon" />
              </div>
              <div>
                <p className="stat-label">Active Guests</p>
                <h3 className="stat-value">{recentReservations.length}</h3>
                <p className="stat-change">
                  <ClockIcon className="w-4 h-4" />
                  Current period
                </p>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="content-grid">
            {/* Recent Reservations */}
            <div className="content-card">
              <div className="card-header">
                <div className="card-title-group">
                  <CalendarIcon className="card-icon" />
                  <div>
                    <h3 className="card-title">Recent Reservations</h3>
                    <p className="card-subtitle">Latest bookings this week</p>
                  </div>
                </div>
                <span className="time-badge">Weekly</span>
              </div>

              <div className="card-body">
                {recentReservations.length === 0 ? (
                  <div className="empty-state">
                    <CalendarIcon className="empty-icon" />
                    <h4 className="empty-title">No Reservations</h4>
                    <p className="empty-text">No recent reservations found for this week</p>
                  </div>
                ) : (
                  <div className="reservations-list">
                    {recentReservations.map((r, idx) => (
                      <div key={r.reservation_number} className="reservation-item">
                        <div className="reservation-avatar">
                          <UserGroupIcon className="w-6 h-6" />
                        </div>
                        <div className="reservation-content">
                          <div className="reservation-header">
                            <h4 className="guest-name">{r.guest_name}</h4>
                            <span className="channel-badge">{r.channel}</span>
                          </div>
                          <div className="reservation-details">
                            <span className="reservation-number">#{r.reservation_number}</span>
                            <span className="date-range">
                              {formatDate(r.arrival)} → {formatDate(r.departure)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Availability Summary */}
            <div className="content-card">
              <div className="card-header">
                <div className="card-title-group">
                  <HomeIcon className="card-icon" />
                  <div>
                    <h3 className="card-title">Room Availability</h3>
                    <p className="card-subtitle">Weekly availability status</p>
                  </div>
                </div>
                <span className="time-badge">Weekly</span>
              </div>

              {availabilityError && (
                <div className="warning-alert">
                  <ExclamationCircleIcon className="w-5 h-5" />
                  <span>Availability data unavailable: {availabilityError}</span>
                </div>
              )}

              <div className="card-body">
                {!availabilitySummary || availabilitySummary.summary.length === 0 ? (
                  <div className="empty-state">
                    <HomeIcon className="empty-icon" />
                    <h4 className="empty-title">No Availability Data</h4>
                    <p className="empty-text">Unable to load availability information</p>
                  </div>
                ) : (
                  <div className="availability-grid">
                    {availabilitySummary.summary.map((d) => (
                      <div key={d.date} className="availability-card">
                        <div className="availability-header">
                          <div>
                            <p className="availability-label">{d.label}</p>
                            <p className="availability-date">{d.date}</p>
                          </div>
                          <span className={`availability-badge ${d.availableCount > 0 ? 'available' : 'unavailable'}`}>
                            {d.availableCount}
                          </span>
                        </div>
                        <div className="availability-footer">
                          {d.availableCount > 0 ? (
                            <span className="status-text status-available">
                              <CheckCircleIcon className="w-4 h-4" />
                              Available
                            </span>
                          ) : (
                            <span className="status-text status-full">
                              <ExclamationCircleIcon className="w-4 h-4" />
                              Fully Booked
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Styles */}
      <style jsx>{`
        .overview-container {
          padding: 0;
        }

        .overview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.25rem 0;
        }

        .section-subtitle {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0;
        }

        .refresh-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1.25rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3);
        }

        .refresh-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
        }

        .refresh-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
        }

        .spinner {
          width: 3rem;
          height: 3rem;
          border: 4px solid #e5e7eb;
          border-top-color: #8b5cf6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .loading-text {
          margin-top: 1rem;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .error-alert {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 1.25rem;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 0.75rem;
          color: #dc2626;
          font-size: 0.875rem;
          margin-bottom: 1.5rem;
        }

        .warning-alert {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 0.75rem;
          color: #d97706;
          font-size: 0.875rem;
          margin: 1rem 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: all 0.3s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .stat-icon-wrapper {
          width: 3.5rem;
          height: 3.5rem;
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-card-blue .stat-icon-wrapper {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
        }

        .stat-card-green .stat-icon-wrapper {
          background: linear-gradient(135deg, #10b981, #059669);
        }

        .stat-card-purple .stat-icon-wrapper {
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
        }

        .stat-icon {
          width: 1.75rem;
          height: 1.75rem;
          color: white;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 0.25rem 0;
        }

        .stat-value {
          font-size: 1.875rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .stat-change {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.75rem;
          color: #10b981;
          font-weight: 600;
        }

        .content-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
          gap: 1.5rem;
        }

        .content-card {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid #f3f4f6;
          background: linear-gradient(135deg, #f9fafb, #ffffff);
        }

        .card-title-group {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .card-icon {
          width: 1.5rem;
          height: 1.5rem;
          color: #8b5cf6;
        }

        .card-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
        }

        .card-subtitle {
          font-size: 0.75rem;
          color: #9ca3af;
          margin: 0;
        }

        .time-badge {
          padding: 0.375rem 0.75rem;
          background: #eff6ff;
          color: #2563eb;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .card-body {
          padding: 1.5rem;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem 1rem;
          text-align: center;
        }

        .empty-icon {
          width: 4rem;
          height: 4rem;
          color: #d1d5db;
          margin-bottom: 1rem;
        }

        .empty-title {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 0.5rem 0;
        }

        .empty-text {
          font-size: 0.875rem;
          color: #9ca3af;
          margin: 0;
        }

        .reservations-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .reservation-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: #f9fafb;
          border-radius: 0.75rem;
          border: 1px solid #e5e7eb;
          transition: all 0.2s ease;
        }

        .reservation-item:hover {
          background: white;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .reservation-avatar {
          width: 3rem;
          height: 3rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .reservation-content {
          flex: 1;
          min-width: 0;
        }

        .reservation-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.375rem;
        }

        .guest-name {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          truncate: true;
        }

        .channel-badge {
          padding: 0.25rem 0.625rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 0.375rem;
          font-size: 0.6875rem;
          font-weight: 600;
        }

        .reservation-details {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8125rem;
          color: #6b7280;
        }

        .reservation-number {
          font-weight: 500;
        }

        .date-range {
          color: #9ca3af;
        }

        .availability-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
        }

        .availability-card {
          padding: 1rem;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          transition: all 0.2s ease;
        }

        .availability-card:hover {
          background: white;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .availability-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.75rem;
        }

        .availability-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 0.25rem 0;
        }

        .availability-date {
          font-size: 0.75rem;
          color: #9ca3af;
          margin: 0;
        }

        .availability-badge {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.125rem;
        }

        .availability-badge.available {
          background: #d1fae5;
          color: #065f46;
        }

        .availability-badge.unavailable {
          background: #f3f4f6;
          color: #6b7280;
        }

        .availability-footer {
          border-top: 1px solid #e5e7eb;
          padding-top: 0.75rem;
        }

        .status-text {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .status-available {
          color: #059669;
        }

        .status-full {
          color: #6b7280;
        }

        @media (max-width: 1024px) {
          .content-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 640px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .overview-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .refresh-button {
            width: 100%;
            justify-content: center;
          }

          .availability-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Overview;